import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Nullable } from 'global';
import {
  dealBuilderApi,
  CustomerContactsQueryResponse,
  DealBuilderEntity,
  GetDealBuildersResponse,
  GetDealBuildersVariables,
  StartDealBuilderContactInput,
  StartDealBuilderResponse,
  StartDealBuilderVariables,
  DealBuilderQuote,
} from '../api';
import { CalculatedTotalsByLevel } from 'modules/quote';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { collectDealBuilderContactsData } from '../lib';
import { DealBuilderContactsData } from '../types';
import { ProposalCategory } from '@trnsact/trnsact-shared-types';

interface InitialDealBuilderData {
  ownerPgIds: string[];
  accountContactIds: string[];
  equipmentId: Nullable<string>;
  quote: Nullable<DealBuilderQuote>;
  proposal: DealBuilderEntity['proposal'];
  calculatedTotals: CalculatedTotalsByLevel;
}

interface UseDealBuilderPersistenceResult {
  activeEntity: Nullable<DealBuilderEntity>;
  historyEntities: DealBuilderEntity[];
  contactsData: DealBuilderContactsData;
  loading: boolean;
  initialData: Nullable<InitialDealBuilderData>;
  setActiveEntity: (entity: DealBuilderEntity) => void;
  startNewQuote: () => Promise<Nullable<string>>;
}

export const useDealBuilderPersistence = (
  vo: VendorOpportunity,
  activeProposalId: Nullable<string> = null,
  creditApplicationId: Nullable<string> = null
): UseDealBuilderPersistenceResult => {
  const [createdEntity, setCreatedEntity] = useState<Nullable<DealBuilderEntity>>(null);
  const startingRef = useRef(false);

  const { vendorOpportunityId, potentialCustomer } = vo;

  const {
    data: queryData,
    loading: dealBuildersLoading,
    refetch,
  } = useQuery<GetDealBuildersResponse, GetDealBuildersVariables>(dealBuilderApi.getDealBuilders, {
    variables: { vendorOpportunityId },
  });

  const { data: contactsQueryData, loading: contactsLoading } = useQuery<CustomerContactsQueryResponse>(
    dealBuilderApi.getCustomerContacts,
    {
      variables: { customerAccountId: potentialCustomer?.id ?? null },
      skip: !potentialCustomer?.id,
    }
  );

  const [startDealBuilderMutation, { loading: isStartDealBuilderLoading }] = useMutation<
    StartDealBuilderResponse,
    StartDealBuilderVariables
  >(dealBuilderApi.startDealBuilder);

  const loading = dealBuildersLoading || contactsLoading || isStartDealBuilderLoading;
  const contacts = contactsQueryData?.customer?.customerContacts?.list ?? [];

  const contactsData = useMemo(
    () => collectDealBuilderContactsData(vo, contacts, creditApplicationId),
    [vo, contacts, creditApplicationId]
  );

  const fetchedEntities = queryData?.dealBuilder.getDealBuilders.entity ?? [];
  const defaultEntity = fetchedEntities.find(p => p.proposal.category === ProposalCategory.DealBuilder) ?? null;
  const overrideEntity = activeProposalId
    ? (fetchedEntities.find(e => e.proposalId === activeProposalId) ?? null)
    : null;
  const activeEntity = createdEntity ?? overrideEntity ?? defaultEntity;

  const historyEntities = useMemo(() => {
    if (!activeEntity) return [];
    return fetchedEntities.filter(e => e.proposalId !== activeEntity.proposalId);
  }, [fetchedEntities, activeEntity]);

  const initialData = useMemo((): Nullable<InitialDealBuilderData> => {
    if (!activeEntity) return null;

    const ownerPgIds = (activeEntity.proposalContacts ?? []).filter(c => c.ownerPgId).map(c => c.ownerPgId!);
    const accountContactIds = (activeEntity.proposalContacts ?? [])
      .filter(c => c.accountContactId)
      .map(c => c.accountContactId!);

    const equipmentId = activeEntity.proposalEquipments?.[0]?.equipmentId ?? null;

    const quote = activeEntity.quote ?? null;
    const proposal = activeEntity.proposal;

    const calculatedTotals: CalculatedTotalsByLevel = {
      invoice: { fees: activeEntity.quote?.feesTotal ?? 0, taxes: 0, price: 0 },
      equipments: { fees: 0, taxes: 0, price: quote?.equipmentSubtotal ?? 0 },
      attachments: { fees: 0, taxes: 0, price: 0 },
    };
    return { ownerPgIds, accountContactIds, equipmentId, quote, proposal, calculatedTotals };
  }, [activeEntity]);

  const createDealBuilder = useCallback(
    async (contacts: StartDealBuilderContactInput[]) => {
      if (!vendorOpportunityId) return null;
      const { data } = await startDealBuilderMutation({
        variables: { input: { vendorOpportunityId, ...(contacts.length ? { contacts } : {}) } },
      });
      const entity = data?.dealBuilder.startDealBuilder.entity ?? null;
      if (entity) {
        //setCreatedEntity(entity as DealBuilderEntity);
        refetch();
      }
      return entity;
    },
    [vendorOpportunityId, startDealBuilderMutation, refetch]
  );

  // Auto-create entity on initial load if none exists
  useEffect(() => {
    if (loading || activeEntity || startingRef.current || !vendorOpportunityId) return;
    startingRef.current = true;
    const primaryOwnerPgId = contactsData.primaryContact?.ownerPgId;
    const contactInputs: StartDealBuilderContactInput[] = primaryOwnerPgId ? [{ ownerPgId: primaryOwnerPgId }] : [];
    createDealBuilder(contactInputs);
  }, [loading, activeEntity, vendorOpportunityId, contactsData, createDealBuilder]);

  const startNewQuote = useCallback(async (): Promise<Nullable<string>> => {
    const primaryOwnerPgId = contactsData.primaryContact?.ownerPgId;
    const contactInputs: StartDealBuilderContactInput[] = primaryOwnerPgId ? [{ ownerPgId: primaryOwnerPgId }] : [];
    const entity = await createDealBuilder(contactInputs);
    return entity?.proposalId ?? null;
  }, [contactsData, createDealBuilder]);

  return {
    activeEntity,
    historyEntities,
    contactsData,
    loading,
    initialData,
    setActiveEntity: setCreatedEntity,
    startNewQuote,
  };
};
